import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '@azure/msal-react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import {
  Archive,
  Check,
  Download,
  History,
  type LucideIcon,
  Rocket,
  Save,
  SquarePen,
  Trash2,
  Upload,
} from 'lucide-react';

import {
  activateWorkflowVersion,
  compareWorkflowVersions,
  createWorkflowVersion,
  deleteWorkflowVersion,
  getMeta,
  getWorkflow,
  getWorkflowVersionValidation,
  getWorkflowsCollection,
  toWorkflowActor,
  updateWorkflowVersion,
} from '$api/WorkflowsAPI';
import { AdminShell } from '$layouts/v2/AdminShell';
import { AdminMeta } from '$layouts/v2/components/types';
import { WorkflowEditor } from '$layouts/v2/components/WorkflowEditor';
import { WorkflowActivationReviewDialog } from './WorkflowActivationReviewDialog';
import { useAccessToken } from '$src/authConfig';
import { useUnsavedChangesWarning } from '$src/hooks/use-unsaved-changes-warning';

type VersionStatusPresentation = {
  label: string;
  icon: LucideIcon;
  chipColor: 'default' | 'success' | 'warning';
  iconColor: string;
};

const VERSION_STATUS: Record<
  v4.WorkflowVersionSpec['status'],
  VersionStatusPresentation
> = {
  draft: {
    label: 'Draft',
    icon: SquarePen,
    chipColor: 'warning',
    iconColor: 'warning.main',
  },
  active: {
    label: 'Active',
    icon: Rocket,
    chipColor: 'success',
    iconColor: 'success.main',
  },
  archived: {
    label: 'Archived',
    icon: Archive,
    chipColor: 'default',
    iconColor: 'text.secondary',
  },
};

function getVersionStatusPresentation(
  version: v4.WorkflowVersionSpec,
  activeVersion: number | null | undefined,
): VersionStatusPresentation {
  const status = VERSION_STATUS[version.status];

  return version.version === activeVersion
    ? {
        ...status,
        label: 'Live',
        chipColor: 'success',
        iconColor: 'success.main',
      }
    : status;
}

function formatAuditDate(value: string | undefined): string {
  if (!value) return 'Not recorded';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatActor(actor: v4.WorkflowActor | null | undefined): string {
  if (!actor) return 'Not recorded';
  const name = actor.name?.trim();
  const username = actor.username?.trim();
  if (name && username && name !== username) return `${name} (${username})`;
  return name || username || actor.id || 'Not recorded';
}

type WorkflowExport = {
  format: 'rotranext-workflow';
  formatVersion: 1;
  workflow: {
    id: string;
    trigger: v4.WorkflowHeadSpec['trigger'];
  };
  version: {
    number: number;
    status: v4.WorkflowVersionSpec['status'];
    title: string;
    description: string;
  };
  spec: v4.WorkflowSpec;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function readWorkflowSpec(value: unknown): v4.WorkflowSpec {
  const candidate = isRecord(value) && 'spec' in value ? value.spec : value;

  if (
    !isRecord(candidate) ||
    typeof candidate.initial !== 'string' ||
    !isRecord(candidate.steps)
  ) {
    throw new Error(
      'The JSON must contain a workflow spec with an "initial" string and a "steps" object.',
    );
  }

  if (Object.keys(candidate.steps).length === 0) {
    throw new Error('The workflow must contain at least one step.');
  }

  if (!(candidate.initial in candidate.steps)) {
    throw new Error(
      `The initial step "${candidate.initial}" does not exist in "steps".`,
    );
  }

  for (const [stepId, step] of Object.entries(candidate.steps)) {
    if (!isRecord(step) || typeof step.kind !== 'string') {
      throw new Error(`Step "${stepId}" is missing a valid "kind".`);
    }
  }

  return candidate as unknown as v4.WorkflowSpec;
}

export default function WorkflowDetailPage() {
  const { workflowId = '', version: versionParam } = useParams<{
    workflowId: string;
    version?: string;
  }>();
  const navigate = useNavigate();
  const { token } = useAccessToken();
  const account = useAccount();
  const [head, setHead] = useState<v4.WorkflowHeadSpec | null>(null);
  const [versions, setVersions] = useState<v4.WorkflowVersionSpec[]>([]);
  const [meta, setMeta] = useState<AdminMeta | null>(null);
  const [workflowIds, setWorkflowIds] = useState<string[]>([]);

  const [draft, setDraft] = useState<v4.WorkflowSpec | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [comparison, setComparison] = useState<v4.WorkflowComparison | null>(
    null,
  );
  const [validation, setValidation] =
    useState<v4.WorkflowValidationResult | null>(null);
  const [reviewVersion, setReviewVersion] = useState<number | null>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [activating, setActivating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setError(null);
    if (token) {
      try {
        const [data, m, all] = await Promise.all([
          getWorkflow(token, workflowId),
          getMeta(token),
          getWorkflowsCollection(token),
        ]);
        setHead(data.head);
        setVersions(data.versions);
        setMeta(m);
        setWorkflowIds(
          all.filter((w) => w.trigger === 'workflow').map((w) => w.workflowId),
        );
      } catch (e) {
        setError(String(e));
      }
    }
  }, [workflowId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedVersion = useMemo(() => {
    const requestedVersion = Number(versionParam);
    if (
      Number.isInteger(requestedVersion) &&
      versions.some((version) => version.version === requestedVersion)
    ) {
      return requestedVersion;
    }

    return head?.activeVersion ?? head?.latestVersion ?? null;
  }, [head, versionParam, versions]);

  // Keep legacy and invalid-version URLs reloadable by canonicalising them once
  // the available versions have loaded.
  useEffect(() => {
    if (selectedVersion === null) return;
    const canonicalPath = `/v2/mail-automation/workflows/${encodeURIComponent(
      workflowId,
    )}/versions/${selectedVersion}`;
    if (window.location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [navigate, selectedVersion, workflowId]);

  const current = useMemo(
    () => versions.find((v) => v.version === selectedVersion),
    [versions, selectedVersion],
  );

  useEffect(() => {
    if (!token || !current) {
      setValidation(null);
      return;
    }
    let cancelled = false;
    getWorkflowVersionValidation(token, workflowId, current.version)
      .then((result) => {
        if (!cancelled) {
          setValidation(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setValidation(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [current, token, workflowId]);

  // Load the selected version's spec into the working draft.
  useEffect(() => {
    if (current) {
      setDraft(structuredClone(current.spec));
      setSelectedStepId(Object.keys(current.spec.steps)[0] ?? null);
    }
  }, [current]);

  const isDraft = current?.status === 'draft';
  const existingDraft = versions
    .filter((version) => version.status === 'draft')
    .toSorted((a, b) => b.version - a.version)[0];
  const newDraftTitle = existingDraft
    ? `Draft version ${existingDraft.version} already exists. Select it from the version menu.`
    : 'Create a new draft from this version';
  const newDraftLabel = existingDraft
    ? `Draft v${existingDraft.version} exists`
    : 'New draft';
  const canDeleteDraft = Boolean(isDraft && versions.length > 1);
  const dirty = Boolean(
    current && draft && JSON.stringify(draft) !== JSON.stringify(current.spec),
  );
  const canActivate = Boolean(
    current && head && head.activeVersion !== current.version,
  );
  const confirmDiscardChanges = useUnsavedChangesWarning(dirty);
  let saveButtonLabel = 'All changes saved';
  if (dirty) {
    saveButtonLabel = 'Save changes';
  }
  if (saving) {
    saveButtonLabel = 'Saving…';
  }

  const save = async () => {
    if (!current || !draft) return;
    setSaving(true);
    setError(null);
    try {
      if (token) {
        await updateWorkflowVersion(token, workflowId, current.version, {
          spec: draft,
          actor: toWorkflowActor(account),
        });
        await load();
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const openActivationReview = async (version: number) => {
    if (!token || !workflowId || dirty) return;
    setError(null);
    setReviewVersion(version);
    setComparison(null);
    setValidation(null);
    setLoadingComparison(true);
    try {
      const [nextComparison, nextValidation] = await Promise.all([
        compareWorkflowVersions(token, workflowId, version),
        getWorkflowVersionValidation(token, workflowId, version),
      ]);
      setComparison(nextComparison);
      setValidation(nextValidation);
    } catch (e) {
      setError(String(e));
      setReviewVersion(null);
    } finally {
      setLoadingComparison(false);
    }
  };

  const activate = async () => {
    if (
      !token ||
      !workflowId ||
      !head ||
      reviewVersion === null ||
      !validation?.valid
    ) {
      return;
    }
    setError(null);
    setActivating(true);
    try {
      await activateWorkflowVersion(token, {
        workflowId,
        version: reviewVersion,
        expectedActiveVersion: head.activeVersion,
        actor: toWorkflowActor(account),
      });
      setReviewVersion(null);
      setComparison(null);
      setValidation(null);
      await load();
    } catch (e) {
      setError(String(e));
    } finally {
      setActivating(false);
    }
  };

  const newDraft = async () => {
    if (!token || !workflowId || !selectedVersion) return;
    setError(null);
    try {
      const v = await createWorkflowVersion(token, workflowId, {
        basedOn: selectedVersion,
        actor: toWorkflowActor(account),
      });
      await load();
      navigate(
        `/v2/mail-automation/workflows/${encodeURIComponent(
          workflowId,
        )}/versions/${v.version}`,
      );
    } catch (e) {
      setError(String(e));
    }
  };

  const deleteDraft = async () => {
    if (!token || !current || !isDraft || !canDeleteDraft || dirty) return;

    setError(null);
    setDeleting(true);
    try {
      const remainingVersions = versions
        .filter((version) => version.version !== current.version)
        .toSorted((a, b) => b.version - a.version);
      const nextVersion =
        head?.activeVersion ?? remainingVersions[0]?.version ?? null;

      await deleteWorkflowVersion(token, workflowId, current.version, {
        actor: toWorkflowActor(account),
      });
      setDeleteDialogOpen(false);
      await load();
      if (nextVersion !== null) {
        navigate(
          `/v2/mail-automation/workflows/${encodeURIComponent(
            workflowId,
          )}/versions/${nextVersion}`,
        );
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setDeleting(false);
    }
  };

  const exportWorkflow = () => {
    if (!current || !head) {
      return;
    }

    const exported: WorkflowExport = {
      format: 'rotranext-workflow',
      formatVersion: 1,
      workflow: {
        id: workflowId,
        trigger: head.trigger,
      },
      version: {
        number: current.version,
        status: current.status,
        title: current.title,
        description: current.description,
      },
      spec: structuredClone(draft ?? current.spec),
    };
    const blob = new Blob([`${JSON.stringify(exported, null, 2)}\n`], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const safeId = workflowId.replaceAll(/[^a-zA-Z0-9_-]/g, '-');
    anchor.href = url;
    anchor.download = `${safeId}-v${current.version}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const importWorkflow = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !isDraft) {
      return;
    }

    setError(null);
    setImportMessage(null);
    try {
      const parsed: unknown = JSON.parse(await file.text());
      const importedSpec = readWorkflowSpec(parsed);
      setDraft(structuredClone(importedSpec));
      setSelectedStepId(
        importedSpec.initial || Object.keys(importedSpec.steps)[0] || null,
      );
      setImportMessage(
        `"${file.name}" loaded into this draft. Review the workflow and click Save to persist it.`,
      );
    } catch (error_) {
      setError(
        error_ instanceof SyntaxError
          ? `"${file.name}" is not valid JSON.`
          : `Could not import "${file.name}": ${String(error_)}`,
      );
    }
  };

  return (
    <AdminShell
      title={head ? head.title : 'Workflow'}
      backAction="/v2/mail-automation/workflows"
      actions={
        <>
          {isDraft && (
            <>
              <input
                ref={uploadInputRef}
                hidden
                type="file"
                accept="application/json,.json"
                onChange={(event) => void importWorkflow(event)}
              />
              <Button
                startIcon={<Upload size={14} />}
                onClick={() => uploadInputRef.current?.click()}
              >
                Upload JSON
              </Button>
            </>
          )}
          {current && (
            <Button startIcon={<Download size={14} />} onClick={exportWorkflow}>
              Download JSON
            </Button>
          )}
          {current && !isDraft && (
            <Button
              startIcon={<History size={14} />}
              onClick={() =>
                navigate(
                  `/v2/mail-automation/workflow-runs?workflowId=${encodeURIComponent(
                    workflowId,
                  )}&workflowVersion=${current.version}`,
                )
              }
              title={`View runs for workflow version ${current.version}`}
            >
              View run history
            </Button>
          )}
          {isDraft && (
            <Button
              variant={dirty ? 'contained' : 'outlined'}
              color={dirty ? 'warning' : 'success'}
              startIcon={dirty ? <Save size={14} /> : <Check size={14} />}
              onClick={save}
              disabled={saving || !dirty}
              title={
                dirty
                  ? 'This draft has changes that have not been saved'
                  : 'This draft matches the saved version'
              }
            >
              {saveButtonLabel}
            </Button>
          )}
          {isDraft && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 size={14} />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleting || saving || dirty || versions.length <= 1}
              title={
                dirty
                  ? 'Save or discard unsaved changes before deleting this draft'
                  : versions.length <= 1
                  ? 'The only workflow version cannot be deleted'
                  : 'Delete this draft version'
              }
            >
              {deleting ? 'Deleting…' : 'Delete draft'}
            </Button>
          )}
          {current && !isDraft && (
            <Button
              onClick={newDraft}
              startIcon={<SquarePen size={14} />}
              disabled={Boolean(existingDraft)}
              title={newDraftTitle}
            >
              {newDraftLabel}
            </Button>
          )}
          {canActivate ? (
            <Button
              variant="contained"
              startIcon={<Rocket size={14} />}
              color="primary"
              onClick={() =>
                current && void openActivationReview(current.version)
              }
              disabled={dirty || loadingComparison || activating}
              title={
                dirty
                  ? 'Save changes before reviewing activation'
                  : 'Review the changes before making this version live'
              }
            >
              {loadingComparison
                ? 'Preparing…'
                : isDraft
                ? 'Review activation'
                : 'Review re-activation'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Rocket size={14} />}
              color="primary"
              disabled
              title="This version is already active"
            >
              Active
            </Button>
          )}
          {current && (
            <Select<number>
              size="small"
              value={current.version}
              onChange={(event) => {
                if (confirmDiscardChanges()) {
                  navigate(
                    `/v2/mail-automation/workflows/${encodeURIComponent(
                      workflowId,
                    )}/versions/${event.target.value}`,
                  );
                }
              }}
              aria-label="Workflow version"
              renderValue={(value) => {
                const version = versions.find(
                  (candidate) => candidate.version === value,
                );
                if (!version) {
                  return `Version ${value}`;
                }

                const status = getVersionStatusPresentation(
                  version,
                  head?.activeVersion,
                );
                const StatusIcon = status.icon;

                return (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{
                      alignItems: 'center',
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: 'grid', color: status.iconColor }}
                    >
                      <StatusIcon
                        size={16}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </Box>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                    >
                      Version {version.version}
                    </Typography>
                    <Chip
                      label={status.label}
                      size="small"
                      color={status.chipColor}
                      variant={status.label === 'Live' ? 'filled' : 'outlined'}
                      sx={{ pointerEvents: 'none' }}
                    />
                  </Stack>
                );
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      mt: 0.75,
                      minWidth: 250,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 8px 24px rgba(1, 4, 9, 0.35)',
                    },
                  },
                  list: { sx: { py: 0.5 } },
                },
              }}
              sx={{
                minWidth: { xs: 155, sm: 210 },
                bgcolor: 'background.default',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: '5px !important',
                },
              }}
            >
              {versions
                .toSorted((a, b) => b.version - a.version)
                .map((version) => {
                  const isLive = version.version === head?.activeVersion;
                  const isSelected = version.version === current.version;
                  const status = getVersionStatusPresentation(
                    version,
                    head?.activeVersion,
                  );
                  const StatusIcon = status.icon;

                  return (
                    <MenuItem
                      key={version.id}
                      value={version.version}
                      sx={{
                        gap: 1,
                        minHeight: 54,
                        mx: 0.5,
                        px: 1,
                        py: 0.75,
                        width: 'calc(100% - 8px)',
                        borderRadius: 1,
                        '&.Mui-selected': {
                          bgcolor: 'action.selected',
                          '&:hover': { bgcolor: 'action.selected' },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          display: 'grid',
                          flexShrink: 0,
                          placeItems: 'center',
                          borderRadius: 1,
                          bgcolor: isLive
                            ? 'rgba(87, 171, 90, 0.15)'
                            : 'action.hover',
                          color: status.iconColor,
                        }}
                      >
                        <StatusIcon
                          size={16}
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          sx={{
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontWeight: 600 }}
                          >
                            Version {version.version}
                          </Typography>
                          {isLive && (
                            <Chip
                              label="Live"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {isLive ? 'Currently active' : status.label} · changed
                          by {formatActor(version.updatedBy)}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Box
                          sx={{
                            display: 'grid',
                            flexShrink: 0,
                            placeItems: 'center',
                            color: 'secondary.main',
                          }}
                        >
                          <Check
                            size={17}
                            strokeWidth={2.5}
                            aria-hidden="true"
                          />
                        </Box>
                      )}
                    </MenuItem>
                  );
                })}
            </Select>
          )}
        </>
      }
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '& *': {
            flexShrink: 1,
          },
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {error}
          </Alert>
        )}

        {importMessage && (
          <Alert
            severity="success"
            onClose={() => setImportMessage(null)}
            sx={{ mb: 2 }}
          >
            {importMessage}
          </Alert>
        )}

        {!isDraft && current && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This version is <strong>{current.status}</strong> and read-only. Use
            “New draft” to make changes.
          </Alert>
        )}

        {dirty && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save the draft before reviewing or activating it. The activation
            review always uses the saved version.
          </Alert>
        )}

        {validation?.valid === false && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              This saved version cannot be activated yet
            </Typography>
            <Box component="ul" sx={{ mb: 0, mt: 0.5, pl: 2.5 }}>
              {validation.issues.map((issue, index) => (
                <li
                  key={`${issue.code}-${issue.dependencyPath.join(
                    '-',
                  )}-${index}`}
                >
                  {issue.message}
                  {issue.rootWorkflowId === workflowId
                    ? ''
                    : ` Affected live workflow: ${issue.rootWorkflowId}.`}
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {current && (
          <Card variant="outlined" sx={{ mb: 2, p: 1.5 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1, md: 3 }}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Workflow created by
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatActor(head?.createdBy)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {formatAuditDate(head?.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Version {current.version} created by
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatActor(current.createdBy)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {formatAuditDate(current.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Last changed by
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatActor(current.updatedBy)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {formatAuditDate(current.updatedAt)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Activated by
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatActor(current.activatedBy)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {formatAuditDate(current.activatedAt)}
                </Typography>
              </Box>
            </Stack>
          </Card>
        )}

        {meta && draft && head && (
          <WorkflowEditor
            spec={draft}
            meta={meta}
            workflowId={workflowId}
            title={head.title}
            trigger={head.trigger}
            workflowIds={workflowIds}
            validationIssues={validation?.issues}
            selectedStepId={selectedStepId}
            readOnly={!isDraft}
            onSelectStep={setSelectedStepId}
            onChange={(nextDraft) => {
              setDraft(nextDraft);
              setValidation(null);
            }}
          />
        )}
      </Box>
      <WorkflowActivationReviewDialog
        open={reviewVersion !== null}
        comparison={comparison}
        validation={validation}
        title={current?.title ?? head?.title ?? workflowId}
        onClose={() => {
          if (!activating) {
            setReviewVersion(null);
            setComparison(null);
            setValidation(null);
          }
        }}
        onConfirm={() => void activate()}
        confirming={activating}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleting) setDeleteDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete draft version?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Version {current?.version} will be permanently removed from this
            workflow. It cannot be activated or restored afterward. The live
            version and all other versions will remain unchanged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void deleteDraft()}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={<Trash2 size={16} />}
          >
            {deleting ? 'Deleting…' : 'Delete draft'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminShell>
  );
}
